import React from 'react';
import { ShieldCheck, FileText, Wifi, Info } from 'lucide-react';
import { findRegulation, KYC_TONE } from '../data/regulations';
import Flag from './Flag';

/**
 * Inline regulations panel — used inside Order Form modal and the
 * standalone regulation popup dialog.
 */
export default function RegulationsPanel({ country, type, code, compact = false }) {
  const reg = findRegulation(country, type);
  if (!reg) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <p className="flex items-center gap-2 font-semibold text-slate-700 mb-1">
          <Info size={14} className="text-violet-500" /> Regulations
        </p>
        <p className="text-xs text-slate-500">
          Specific regulations vary. Our team will confirm requirements with you upon order.
        </p>
      </div>
    );
  }

  const tone = KYC_TONE[reg.kyc] || KYC_TONE['Worldwide'];

  return (
    <div className={`rounded-2xl border border-slate-200 bg-white overflow-hidden ${compact ? '' : 'shadow-sm'}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-violet-50 via-purple-50 to-fuchsia-50 border-b border-slate-100">
        <div className="flex items-center gap-3 min-w-0">
          {code && <Flag code={code} size="sm" />}
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Regulations</p>
            <p className="font-bold text-slate-900 truncate">{reg.country}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${tone.bg} ${tone.text} ${tone.border}`}>
          <ShieldCheck size={12} /> KYC: {tone.label}
        </span>
      </div>

      {/* Body */}
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
        <Section icon={FileText} title="Required documents">
          <ul className="space-y-1.5">
            {reg.documents.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-500 mt-2 flex-shrink-0" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={Wifi} title="Reachability & notes">
          <p className="text-sm text-slate-700 leading-relaxed">{reg.reachability}</p>
        </Section>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="p-4">
      <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">
        <Icon size={12} className="text-violet-500" /> {title}
      </p>
      {children}
    </div>
  );
}

/**
 * Mini KYC pill — used inline in the pricing table.
 */
export function KycPill({ country, type }) {
  const reg = findRegulation(country, type);
  if (!reg) return null;
  const tone = KYC_TONE[reg.kyc] || KYC_TONE['Worldwide'];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${tone.bg} ${tone.text} ${tone.border}`}>
      <ShieldCheck size={10} /> {tone.label}
    </span>
  );
}
